import { useEffect, useMemo, useState } from "react";
import { Camera, ChefHat, ScanBarcode, PenLine, Search, Sparkles } from "lucide-react";
import { computeFrequent, computeRecent, searchMealHistory } from "../lib/suggestions";
import { searchProducts, type SearchResult } from "../lib/barcode/openfoodfacts";
import { searchUsdaFoods } from "../lib/barcode/usda";
import { gramsForGenericFood, searchGenericFoods, type GenericFood } from "../lib/generic-foods";
import { fetchSuggestions, type Suggestion as AiSuggestion } from "../lib/ai";
import { friendlyError } from "../lib/errors";
import QuantityConfirm from "./QuantityConfirm";
import type { Favorite } from "../lib/favorites";
import type { Goals } from "../lib/goals";
import {
  MEAL_TYPE_ICONS,
  MEAL_TYPE_LABELS,
  scaleEstimate,
  sumTotals,
  type FoodItem,
  type LoggedMeal,
  type MealEstimate,
  type MealType
} from "../lib/types";

type Pending =
  | { kind: "meal"; data: LoggedMeal }
  | { kind: "favorite"; data: Favorite }
  | { kind: "search"; data: SearchResult }
  | { kind: "generic"; data: GenericFood }
  | { kind: "ai"; data: AiSuggestion };

interface Props {
  mealType: MealType;
  meals: LoggedMeal[];
  favorites: Favorite[];
  goals: Goals;
  todayMeals: LoggedMeal[];
  onCancel: () => void;
  onChoosePhoto: () => void;
  onChooseBarcode: () => void;
  onChooseDescribe: () => void;
  onChooseRecipe: () => void;
  onQuickLogMeal: (meal: LoggedMeal, quantity: number) => void;
  onQuickLogFavorite: (favorite: Favorite, quantity: number) => void;
  onQuickLogSearchResult: (result: SearchResult, quantity: number) => void;
  onQuickLogGeneric: (food: GenericFood, quantity: number) => void;
  onQuickLogAiSuggestion: (s: AiSuggestion, quantity: number) => void;
  /** "Edit details →" link in QuantityConfirm: opens MealReview pre-populated
   *  with the chosen item's MealEstimate, scaled by the user's current
   *  quantity selection. Lets users tweak per-item portion text, switch
   *  units via the universal wheel, and edit macros — same screen they'd
   *  see after an AI estimate. */
  onEditAsReview: (estimate: import("../lib/types").MealEstimate, mealType: MealType, source: string) => void;
}

export default function AddFoodPicker({
  mealType,
  meals,
  favorites,
  goals,
  todayMeals,
  onCancel,
  onChoosePhoto,
  onChooseBarcode,
  onChooseDescribe,
  onChooseRecipe,
  onQuickLogMeal,
  onQuickLogFavorite,
  onQuickLogSearchResult,
  onQuickLogGeneric,
  onQuickLogAiSuggestion,
  onEditAsReview
}: Props) {
  const [aiSuggestions, setAiSuggestions] = useState<AiSuggestion[] | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);

  function confirmPending(quantity: number) {
    if (!pending) return;
    switch (pending.kind) {
      case "meal":
        onQuickLogMeal(pending.data, quantity);
        break;
      case "favorite":
        onQuickLogFavorite(pending.data, quantity);
        break;
      case "search":
        onQuickLogSearchResult(pending.data, quantity);
        break;
      case "generic":
        onQuickLogGeneric(pending.data, quantity);
        break;
      case "ai":
        onQuickLogAiSuggestion(pending.data, quantity);
        break;
    }
    setPending(null);
  }

  /** Open MealReview for the pending item, pre-scaled by the user's current
   *  quantity selection. Builds a MealEstimate from whichever kind of pending
   *  the user picked, scales it, and hands it to the parent for the stage
   *  transition. The picker closes itself so we don't end up with both
   *  surfaces visible at once. */
  function editPendingAsReview(scaleFactor: number) {
    if (!pending) return;
    const estimate = buildEstimateForReview(pending);
    if (!estimate) return;
    const scaled = scaleFactor === 1 ? estimate : scaleEstimate(estimate, scaleFactor);
    const source = sourceForPending(pending);
    setPending(null);
    onEditAsReview(scaled, mealType, source);
  }

  async function askAi() {
    setAiLoading(true);
    setAiError(null);
    try {
      const todayTotals = sumTotals(todayMeals);
      const remaining = {
        calories: Math.max(0, goals.calories - todayTotals.calories),
        protein_g: Math.max(0, goals.protein_g - todayTotals.protein_g),
        carbs_g: Math.max(0, goals.carbs_g - todayTotals.carbs_g),
        fat_g: Math.max(0, goals.fat_g - todayTotals.fat_g)
      };
      const s = await fetchSuggestions({ remaining, meal_type: mealType });
      setAiSuggestions(s);
    } catch (e) {
      setAiError(friendlyError(e));
    } finally {
      setAiLoading(false);
    }
  }
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  // USDA results live in a sibling section so the UI can render them
  // alongside OFF without merging (which would require fuzzy dedup we
  // don't want to maintain). USDA failures degrade silently — we still
  // have OFF + generics.
  const [usdaResults, setUsdaResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const trimmed = query.trim();
  const hasQuery = trimmed.length >= 2;

  const generics = useMemo(() => (hasQuery ? searchGenericFoods(trimmed, 6) : []), [trimmed, hasQuery]);

  // "You've had this before" — searches the user's logged meal history for
  // items whose names match the query. Surfaces previously-scanned barcodes
  // (and any other item they've logged) without needing to re-scan. The
  // user-reported pain: "I scanned Diet Coke yesterday, why doesn't it show
  // up when I search 'diet coke' today?" — this is the fix.
  const previouslyEaten = useMemo(
    () => (hasQuery ? searchMealHistory(meals, trimmed, 5) : []),
    [meals, trimmed, hasQuery]
  );

  useEffect(() => {
    if (!hasQuery) {
      setResults([]);
      setUsdaResults([]);
      setSearchError(null);
      return;
    }
    setSearching(true);
    setSearchError(null);
    const timer = setTimeout(async () => {
      // Fire OFF + USDA in parallel; whichever resolves first paints first.
      // Each handles its own errors so one failure doesn't sink the other.
      const offPromise = searchProducts(trimmed)
        .then(setResults)
        .catch((err) => setSearchError((err as Error)?.message ?? String(err)));
      const usdaPromise = searchUsdaFoods(trimmed, 5)
        .then(setUsdaResults)
        .catch(() => {
          /* USDA failures degrade quietly — OFF still has the user covered */
        });
      Promise.allSettled([offPromise, usdaPromise]).finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [trimmed, hasQuery]);

  const frequent = useMemo(
    () => computeFrequent(meals, { mealType, limit: 5 }),
    [meals, mealType]
  );
  const excludeForRecent = useMemo(
    () => new Set(frequent.map((f) => f.signature)),
    [frequent]
  );
  const recent = useMemo(
    () => computeRecent(meals, 5, excludeForRecent),
    [meals, excludeForRecent]
  );

  return (
    <div className="space-y-4 pb-6">
      <header className="px-4 pt-3 flex items-center justify-between">
        <button onClick={onCancel} className="text-sm text-slate-400">
          ← Back
        </button>
        <span className="text-sm text-slate-400">
          Adding to <span className="text-emerald-400">{MEAL_TYPE_LABELS[mealType]}</span>
        </span>
      </header>

      {/* New entry — primary actions. 4 input methods (Photo / Barcode /
          Describe / Recipe). 2x2 grid on mobile, 4-col on tablet+ to keep
          tap targets large. */}
      <div className="px-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <button
          onClick={onChoosePhoto}
          className="py-4 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col items-center gap-1.5 active:bg-slate-800 hover:border-slate-700 transition-colors"
        >
          <Camera className="w-6 h-6 text-emerald-400" strokeWidth={2} />
          <span className="text-xs font-medium text-slate-200">Photo</span>
        </button>
        <button
          onClick={onChooseBarcode}
          className="py-4 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col items-center gap-1.5 active:bg-slate-800 hover:border-slate-700 transition-colors"
        >
          <ScanBarcode className="w-6 h-6 text-emerald-400" strokeWidth={2} />
          <span className="text-xs font-medium text-slate-200">Barcode</span>
        </button>
        <button
          onClick={onChooseDescribe}
          className="py-4 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col items-center gap-1.5 active:bg-slate-800 hover:border-slate-700 transition-colors"
        >
          <PenLine className="w-6 h-6 text-emerald-400" strokeWidth={2} />
          <span className="text-xs font-medium text-slate-200">Describe</span>
        </button>
        <button
          onClick={onChooseRecipe}
          className="py-4 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col items-center gap-1.5 active:bg-slate-800 hover:border-slate-700 transition-colors"
        >
          <ChefHat className="w-6 h-6 text-emerald-400" strokeWidth={2} />
          <span className="text-xs font-medium text-slate-200">Recipe</span>
        </button>
      </div>
      {/* Method picker hint — first-timers don't know which to pick */}
      <p className="px-4 -mt-2 text-[11px] text-slate-400 leading-snug">
        <span className="text-slate-300">Photo</span> for plated meals ·{" "}
        <span className="text-slate-300">Barcode</span> for packaged food ·{" "}
        <span className="text-slate-300">Describe</span> for restaurant meals ·{" "}
        <span className="text-slate-300">Recipe</span> for per-serving recipe macros
      </p>

      {/* Search */}
      <div className="px-4">
        <div className="relative">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Or search foods (e.g. 'greek yogurt')"
            autoComplete="off"
            className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-10 pr-3 py-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" strokeWidth={2} />
        </div>
      </div>

      {/* AI suggestions for remaining macros */}
      <div className="px-4">
        <button
          onClick={askAi}
          disabled={aiLoading}
          className="w-full p-3 rounded-2xl bg-gradient-to-r from-violet-950/60 to-slate-900 border border-violet-800/40 text-sm font-medium text-slate-100 active:opacity-80 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Sparkles className="w-4 h-4 text-violet-300" strokeWidth={2} />
          {aiLoading ? "Thinking…" : "Suggest something that fits the rest of my day"}
        </button>
      </div>
      {aiError && (
        <div className="px-4">
          <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">{aiError}</div>
        </div>
      )}
      {aiSuggestions && aiSuggestions.length > 0 && (
        <Section title="AI suggestions" subtitle="Tap to log" icon={<Sparkles className="w-3.5 h-3.5 text-violet-300" />}>
          <ul className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden divide-y divide-slate-800/60">
            {aiSuggestions.map((s, i) => (
              <li key={i}>
                <button
                  onClick={() => setPending({ kind: "ai", data: s })}
                  className="w-full px-3 py-2.5 flex items-center gap-3 active:bg-slate-800 text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-xl flex-shrink-0">✨</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-100">{s.name}</div>
                    <div className="text-xs text-slate-500">{s.portion} · {s.reason}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-slate-200 tabular-nums">{Math.round(s.calories)}</div>
                    <div className="text-[10px] text-slate-400">kcal</div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {hasQuery ? (
        <>
          {/* You've eaten this before — top-priority section when the query
              matches an item from the user's logged meal history. Solves
              the "where did my scanned barcode go" problem: any food you've
              ever logged is now searchable by name, not just by re-scan. */}
          {previouslyEaten.length > 0 && (
            <Section title="You've had this before" subtitle="From your history">
              <ul className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden divide-y divide-slate-800/60">
                {previouslyEaten.map((s) => {
                  // Find the specific matching item within the representative
                  // meal — that's what we want to surface in title/subtitle.
                  const lq = trimmed.toLowerCase();
                  const matching = s.representative.estimate.items.find((i) =>
                    (i.name ?? "").toLowerCase().includes(lq)
                  ) ?? s.representative.estimate.items[0];
                  const kcal = matching?.calories ?? s.representative.estimate.totals.calories;
                  return (
                    <li key={s.signature}>
                      <PickerRow
                        title={matching?.name ?? "—"}
                        subtitle={`${matching?.portion ?? ""} · logged ${s.count}× before`.trim()}
                        kcal={kcal}
                        photoUrl={s.representative.photo_data_url}
                        fallbackEmoji={MEAL_TYPE_ICONS[s.representative.meal_type] ?? "🍽"}
                        onPick={() => setPending({ kind: "meal", data: s.representative })}
                      />
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}

          {generics.length > 0 && (
            <Section title="Generic foods" subtitle="USDA standard values">
              <ul className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden divide-y divide-slate-800/60">
                {generics.map((g) => (
                  <li key={g.name}>
                    <PickerRow
                      title={g.name}
                      subtitle={g.portion}
                      kcal={g.calories}
                      photoUrl={undefined}
                      fallbackEmoji={g.emoji}
                      onPick={() => setPending({ kind: "generic", data: g })}
                    />
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <SearchSection
            results={results}
            searching={searching}
            error={searchError}
            onPick={(r) => setPending({ kind: "search", data: r })}
          />

          {/* USDA FoodData Central — secondary source for US branded items
              and research-grade whole foods. Renders in its own section so
              users can tell the data lineage without merging requiring
              fuzzy dedup we don't want to maintain. Hidden when empty. */}
          {usdaResults.length > 0 && (
            <Section title="USDA database" subtitle="US food labels + research data">
              <ul className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden divide-y divide-slate-800/60">
                {usdaResults.map((r) => (
                  <li key={`usda-${r.code}`}>
                    <PickerRow
                      title={r.name}
                      subtitle={r.estimate.items[0]?.portion}
                      kcal={r.estimate.totals.calories}
                      photoUrl={r.imageUrl}
                      fallbackEmoji="🇺🇸"
                      onPick={() => setPending({ kind: "search", data: r })}
                    />
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </>
      ) : (
        <>
          {favorites.length > 0 && (
            <Section title="★ Favorites" subtitle="Saved templates">
              <ul className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden divide-y divide-slate-800/60">
                {favorites.slice(0, 6).map((f) => (
                  <li key={f.id}>
                    <PickerRow
                      title={f.name}
                      subtitle={subtitleFromItems(f.estimate.items.map((i) => i.name))}
                      kcal={f.estimate.totals.calories}
                      photoUrl={f.photo_data_url}
                      fallbackEmoji={f.meal_type ? MEAL_TYPE_ICONS[f.meal_type] : "⭐"}
                      onPick={() => setPending({ kind: "favorite", data: f })}
                    />
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {frequent.length > 0 && (
            <Section
              title={`${MEAL_TYPE_ICONS[mealType]} Frequent for ${MEAL_TYPE_LABELS[mealType]}`}
              subtitle="Meals you log often here"
            >
              <ul className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden divide-y divide-slate-800/60">
                {frequent.map((s) => (
                  <li key={s.signature}>
                    <PickerRow
                      title={s.representative.estimate.items.map((i) => i.name).join(", ") || "Untitled"}
                      subtitle={`Logged ${s.count}×`}
                      kcal={s.representative.estimate.totals.calories}
                      photoUrl={s.representative.photo_data_url}
                      fallbackEmoji={MEAL_TYPE_ICONS[mealType]}
                      onPick={() => setPending({ kind: "meal", data: s.representative })}
                    />
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {recent.length > 0 && (
            <Section title="Recent" subtitle="Your last meals across all types">
              <ul className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden divide-y divide-slate-800/60">
                {recent.map((s) => (
                  <li key={s.signature}>
                    <PickerRow
                      title={s.representative.estimate.items.map((i) => i.name).join(", ") || "Untitled"}
                      subtitle={MEAL_TYPE_LABELS[s.representative.meal_type]}
                      kcal={s.representative.estimate.totals.calories}
                      photoUrl={s.representative.photo_data_url}
                      fallbackEmoji={MEAL_TYPE_ICONS[s.representative.meal_type]}
                      onPick={() => setPending({ kind: "meal", data: s.representative })}
                    />
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {frequent.length === 0 && recent.length === 0 && favorites.length === 0 && (
            <div className="px-4 text-sm text-slate-500">
              No history yet — search above, snap a photo, or scan a barcode to log your first meal.
            </div>
          )}
        </>
      )}

      {pending && (
        <PendingConfirm
          pending={pending}
          onConfirm={confirmPending}
          onCancel={() => setPending(null)}
          onEditDetails={editPendingAsReview}
        />
      )}
    </div>
  );
}

function PendingConfirm({
  pending,
  onConfirm,
  onCancel,
  onEditDetails
}: {
  pending: Pending;
  onConfirm: (qty: number) => void;
  onCancel: () => void;
  onEditDetails: (scaleFactor: number) => void;
}) {
  const d = pickDisplay(pending);
  return (
    <QuantityConfirm
      onEditDetails={onEditDetails}
      title={d.title}
      subtitle={d.subtitle}
      imageUrl={d.imageUrl}
      emoji={d.emoji}
      baseKcal={d.baseKcal}
      baseProtein={d.baseProtein}
      baseCarbs={d.baseCarbs}
      baseFat={d.baseFat}
      gramWeight={d.gramWeight}
      units={d.units}
      ctaLabel={d.ctaLabel}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

function pickDisplay(p: Pending) {
  switch (p.kind) {
    case "generic": {
      const g = p.data;
      return {
        title: g.name,
        subtitle: g.portion,
        emoji: g.emoji,
        imageUrl: undefined,
        baseKcal: g.calories,
        baseProtein: g.protein_g,
        baseCarbs: g.carbs_g,
        baseFat: g.fat_g,
        gramWeight: gramsForGenericFood(g),
        units: g.units,
        ctaLabel: "Log it"
      };
    }
    case "favorite": {
      const f = p.data;
      // For single-item favorites, surface the gram weight so the
      // QuantityConfirm unit picker activates (g toggle, named units if
      // present on the item). Multi-item favorites just use a multiplier.
      const single = f.estimate.items.length === 1 ? f.estimate.items[0] : null;
      return {
        title: f.name,
        subtitle:
          f.estimate.items.length === 1
            ? f.estimate.items[0].portion
            : `${f.estimate.items.length} items`,
        emoji: f.meal_type ? MEAL_TYPE_ICONS[f.meal_type] : "⭐",
        imageUrl: f.photo_data_url,
        baseKcal: f.estimate.totals.calories,
        baseProtein: f.estimate.totals.protein_g,
        baseCarbs: f.estimate.totals.carbs_g,
        baseFat: f.estimate.totals.fat_g,
        gramWeight: single?.grams_per_portion,
        ctaLabel: "Log favorite"
      };
    }
    case "meal": {
      const m = p.data;
      const single = m.estimate.items.length === 1 ? m.estimate.items[0] : null;
      return {
        title: m.estimate.items.map((i) => i.name).join(", ") || "Untitled",
        subtitle: MEAL_TYPE_LABELS[m.meal_type],
        emoji: MEAL_TYPE_ICONS[m.meal_type],
        imageUrl: m.photo_data_url,
        baseKcal: m.estimate.totals.calories,
        baseProtein: m.estimate.totals.protein_g,
        baseCarbs: m.estimate.totals.carbs_g,
        baseFat: m.estimate.totals.fat_g,
        gramWeight: single?.grams_per_portion,
        ctaLabel: "Log again"
      };
    }
    case "search": {
      const r = p.data;
      return {
        title: r.name,
        subtitle: r.brand,
        emoji: "🍽️",
        imageUrl: r.imageUrl,
        baseKcal: r.estimate.totals.calories,
        baseProtein: r.estimate.totals.protein_g,
        baseCarbs: r.estimate.totals.carbs_g,
        baseFat: r.estimate.totals.fat_g,
        gramWeight: r.gramWeight,
        ctaLabel: "Log it"
      };
    }
    case "ai": {
      const s = p.data;
      return {
        title: s.name,
        subtitle: `${s.portion} · ${s.reason}`,
        emoji: "✨",
        imageUrl: undefined,
        baseKcal: s.calories,
        baseProtein: s.protein_g,
        baseCarbs: s.carbs_g,
        baseFat: s.fat_g,
        ctaLabel: "Log suggestion"
      };
    }
  }
}

function SearchSection({
  results,
  searching,
  error,
  onPick
}: {
  results: SearchResult[];
  searching: boolean;
  error: string | null;
  onPick: (r: SearchResult) => void;
}) {
  return (
    <Section title="Branded products" subtitle="Community database · may be international">
      {error && (
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">{error}</div>
      )}
      {searching ? (
        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 text-slate-500 text-sm flex items-center gap-2">
          <span className="inline-block w-4 h-4 border-2 border-slate-700 border-t-emerald-400 rounded-full animate-spin" />
          Searching food database…
        </div>
      ) : results.length === 0 ? (
        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 text-slate-500 text-sm">
          No branded matches. Use a generic above, scan the barcode, or describe it.
        </div>
      ) : (
        <ul className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden divide-y divide-slate-800/60">
          {results.map((r) => (
            <li key={r.code || r.name}>
              <PickerRow
                title={r.name}
                subtitle={r.brand}
                kcal={r.estimate.totals.calories}
                photoUrl={r.imageUrl}
                fallbackEmoji="🍽️"
                onPick={() => onPick(r)}
              />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Section({
  title,
  subtitle,
  icon,
  children
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="px-4 space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold text-slate-200 inline-flex items-center gap-1.5">
          {icon}
          {title}
        </span>
        {subtitle && <span className="text-xs text-slate-500">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

interface PickerRowProps {
  title: string;
  subtitle?: string;
  kcal: number;
  photoUrl?: string;
  fallbackEmoji: string;
  onPick: () => void;
}

function PickerRow({ title, subtitle, kcal, photoUrl, fallbackEmoji, onPick }: PickerRowProps) {
  return (
    <button
      onClick={onPick}
      className="w-full px-3 py-2.5 flex items-center gap-3 active:bg-slate-800 text-left"
    >
      {photoUrl ? (
        <img src={photoUrl} alt="" className="w-10 h-10 rounded-lg object-cover bg-slate-800 flex-shrink-0" />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-xl flex-shrink-0">
          {fallbackEmoji}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-100 truncate">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 truncate">{subtitle}</div>}
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-sm font-semibold text-slate-200 tabular-nums">
          {Math.round(kcal)}
        </div>
        <div className="text-[10px] text-slate-400">kcal</div>
      </div>
    </button>
  );
}

function subtitleFromItems(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  return `${names.length} items`;
}

// ============================================================================
// "Edit details" plumbing — converts a Pending into a MealEstimate that
// MealReview can render. Generic / AI suggestions don't have a full estimate
// object on hand, so we synthesize one from their flat macro fields.
// Search + favorite + meal kinds already carry an estimate; we just unwrap.
// ============================================================================

function buildEstimateForReview(p: Pending): MealEstimate | null {
  switch (p.kind) {
    case "meal":
      return p.data.estimate;
    case "favorite":
      return p.data.estimate;
    case "search":
      return p.data.estimate;
    case "generic": {
      const g = p.data;
      const item: FoodItem = {
        name: g.name,
        portion: g.portion,
        calories: g.calories,
        protein_g: g.protein_g,
        carbs_g: g.carbs_g,
        fat_g: g.fat_g,
        fiber_g: g.fiber_g
      };
      return {
        items: [item],
        totals: {
          calories: g.calories,
          protein_g: g.protein_g,
          carbs_g: g.carbs_g,
          fat_g: g.fat_g,
          fiber_g: g.fiber_g ?? 0
        },
        confidence: "high",
        clarifying_questions: []
      };
    }
    case "ai": {
      const s = p.data;
      const item: FoodItem = {
        name: s.name,
        portion: s.portion,
        calories: s.calories,
        protein_g: s.protein_g,
        carbs_g: s.carbs_g,
        fat_g: s.fat_g
      };
      return {
        items: [item],
        totals: {
          calories: s.calories,
          protein_g: s.protein_g,
          carbs_g: s.carbs_g,
          fat_g: s.fat_g,
          fiber_g: 0
        },
        confidence: "medium",
        clarifying_questions: []
      };
    }
  }
}

function sourceForPending(p: Pending): string {
  switch (p.kind) {
    case "meal":     return "edit-relog";
    case "favorite": return "edit-favorite";
    case "search":   return p.data.code ? `edit-search:${p.data.code}` : "edit-search";
    case "generic":  return `edit-generic:${p.data.name.toLowerCase()}`;
    case "ai":       return "edit-ai";
  }
}
